import { isBefore, parse } from "date-fns";
import { type NextPage } from "next";
import { AuthedLayout } from "@/layouts";
import { Flex, Stack } from "@chakra-ui/react";
import { Typography } from "@/components/essentials/typography";
import { UserInfo } from "@/components/fragments/userinfo";
import { useGetFamilyCustomerAddressesPagesSettingsShipmentQuery } from "./__gql__/get-family-customer-address.gen";
import { NextSeo } from "next-seo";
import { useGetMePagesSettingsShipmentQuery } from "./__gql__/get-me.gen";
import { Skeleton } from "@/components/fragments/apollo/loader/skeleton";
import { isQueryCompleted } from "@/modules/utils/apollo-wrapper";
import { PrimaryFamilyCustomerUserName } from "./__components__/primary-family-customer-user-name/";
import { FamilyCustomerAddressLinkList } from "./__components__/family-customer-address-link-list/";
import { FamilyCustomerAddressCreateLink } from "./__components__/family-customer-address-create-link/";
import { useApolloError } from "@/hooks/useApolloError";
import { type FamilyCustomerAddress } from "@/gql/generated/graphql.gen";
import { useMemo } from "react";
import { shipmentAplloErrorTestId, shipmentUserInfoTestIdPrefix } from "./const";
import { useGetShipmentsPagesSettingsShipmentQuery } from "./__gql__/get-shipments.gen";
import {
  SHIPMENT_STATUS_IN_PROGRESS_PLAN,
  SHIPMENT_STATUS_WAITING_FOR_REVIEW,
  SHIPMENT_STATUS_COMPLETE_PLAN,
  SHIPMENT_STATUS_RESERVED,
} from "@/configs/uuids/shipments/statuses";
import { DisableNotification } from "./__components__/disable-notification/DisableNotification";
import { isPreparing } from "@/modules/utils/shipments";
import { isNonNullable } from "@/modules/utils/predicate";

export const sortAndFilterResultFamilyCustomerAddresses = (
  familyCustomerAddresses: FamilyCustomerAddress[] | null,
  mail: string,
): FamilyCustomerAddress[] | undefined => {
  return familyCustomerAddresses
    ?.slice()
    .filter((address) => address.mail === mail)
    .sort((prev, current) => {
      // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
      if (prev.createdAt === undefined || prev.createdAt === null) return 1;
      // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
      if (current.createdAt === undefined || current.createdAt === null) return -1;
      if (
        isBefore(
          parse(prev.createdAt, "yyyy-MM-dd HH:mm:ss", new Date()),
          parse(current.createdAt, "yyyy-MM-dd HH:mm:ss", new Date()),
        )
      ) {
        return 1;
      }
      return -1;
    })
    .sort((prev) => {
      // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
      if (prev.isPrimary) return -1;
      return 1;
    });
};

const ShipmentPage: NextPage = () => {
  const resultGetMeQuery = useGetMePagesSettingsShipmentQuery();

  const resultFamilyCustomerAddressQuery = useGetFamilyCustomerAddressesPagesSettingsShipmentQuery({
    skip: resultGetMeQuery.loading,
    variables: {
      isReceiver: resultGetMeQuery.data?.me.isReceiver === true,
    },
  });

  const resultGetShipmentsQuery = useGetShipmentsPagesSettingsShipmentQuery({
    skip: resultGetMeQuery.loading || resultGetMeQuery.data?.me.isReceiver === false,
  });

  const sortedResultFamilyCustomerAddresses = useMemo(() => {
    // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
    if (
      resultFamilyCustomerAddressQuery.data?.familyCustomer.familyCustomerAddresses === undefined ||
      resultGetMeQuery.data?.me.mail === undefined
    )
      return;
    return sortAndFilterResultFamilyCustomerAddresses(
      resultFamilyCustomerAddressQuery.data.familyCustomer.familyCustomerAddresses,
      resultGetMeQuery.data.me.mail,
    );
  }, [resultFamilyCustomerAddressQuery, resultGetMeQuery]);

  const isAddressChangeable = useMemo(() => {
    // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
    if (!isNonNullable(resultGetShipmentsQuery.data)) return;

    // 初回発送後か
    const afterFirstShipment = resultGetShipmentsQuery.data.shipments.some((contract) =>
      contract.shipments.some(
        // 発送された発送が一つでも存在する = 初回発送後
        (shipment) => isNonNullable(shipment.shipmentStatus) && !isPreparing(shipment.shipmentStatus.uuid),
      ),
    );

    // 初回発送前だったら、住所変更できない
    // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
    if (!afterFirstShipment) return false;

    // 発送準備に入っている発送があるか
    const hasInProgressShipmentStatusShipment = resultGetShipmentsQuery.data.shipments.some((contract) =>
      contract.shipments.some(
        (shipment) =>
          shipment.shipmentStatus?.uuid === SHIPMENT_STATUS_IN_PROGRESS_PLAN ||
          shipment.shipmentStatus?.uuid === SHIPMENT_STATUS_WAITING_FOR_REVIEW ||
          shipment.shipmentStatus?.uuid === SHIPMENT_STATUS_COMPLETE_PLAN ||
          shipment.shipmentStatus?.uuid === SHIPMENT_STATUS_RESERVED,
      ),
    );

    // 発送準備中だったら。住所変更できない
    return !hasInProgressShipmentStatusShipment;
  }, [resultGetShipmentsQuery.data]);

  useApolloError(resultGetMeQuery.error || resultFamilyCustomerAddressQuery.error || resultGetShipmentsQuery.error, {
    testid: shipmentAplloErrorTestId,
  });

  return (
    <>
      <NextSeo title="お届け先の一覧" description="登録されているお届け先の一覧を確認できます。" />
      <AuthedLayout
        px={{ base: "1rem", sm: "1.5rem", md: "3rem", lg: "3rem", xl: "4rem", "2xl": "4rem" }}
        pt="1.5rem"
        bgColor="light.main"
      >
        <Flex flexDir="column">
          <Typography variant="display3">お届け先情報</Typography>
          <Typography variant="text4">現在登録されているおもちゃのお届け先です。</Typography>
        </Flex>

        <Flex mt="1.5rem" flexDir="column" alignItems="center" gridGap={6}>
          {!isQueryCompleted([resultGetMeQuery, resultFamilyCustomerAddressQuery]) && (
            <Skeleton w="100%">
              <UserInfo />
            </Skeleton>
          )}
          {isQueryCompleted([resultGetMeQuery, resultFamilyCustomerAddressQuery]) &&
            resultGetMeQuery.data?.me.isReceiver === false && (
              <PrimaryFamilyCustomerUserName
                primaryFamilyCustomerAddress={
                  resultFamilyCustomerAddressQuery.data?.familyCustomer.primaryFamilyCustomerAddress
                }
              />
            )}
          {isQueryCompleted([resultGetMeQuery, resultFamilyCustomerAddressQuery]) &&
            resultGetMeQuery.data?.me.isReceiver === true &&
            (!isAddressChangeable ? (
              <Stack w="100%" spacing={2}>
                <DisableNotification />
                {sortedResultFamilyCustomerAddresses?.map((address) => {
                  return (
                    <UserInfo
                      key={address.uuid}
                      w="100%"
                      type="simple"
                      user={{
                        lastName: address.lastName ?? "",
                        firstName: address.firstName ?? "",
                        address: `${address.address1 ?? ""}${address.address2 ?? ""}${address.buildingName ?? ""}`,
                      }}
                      data-testid={`${shipmentUserInfoTestIdPrefix}${address.uuid}`}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Stack w="100%" spacing={6}>
                <FamilyCustomerAddressLinkList familyCustomerAddresses={sortedResultFamilyCustomerAddresses} />
                <FamilyCustomerAddressCreateLink />
              </Stack>
            ))}
        </Flex>
      </AuthedLayout>
    </>
  );
};

export default ShipmentPage;
